import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ADJUDICATION_SENSITIVITIES,
  CLUB_VALUES_MAX_LENGTH,
  DEFAULT_ADJUDICATION_SENSITIVITY,
  ORG_PROFILE_MAX_LENGTH,
  orgProfileHasReader,
  type AdjudicationSensitivity,
  type ConnectorView,
  type CreditSummary,
  type OrgSettingsUpdate,
  type OrgSettingsView,
} from '@ni/shared';
import { useOrg } from '../api/app-context';
import { useConnector, useCredits, useOrgSettings, useUpdateOrgSettings } from '../api/hooks';
import { loadErrorLine } from '../api/errors';
import {
  ADJUDICATION_SENSITIVITY_LABELS,
  chargeStanding,
  creditAmount,
  CREDIT_OPERATION_LABELS,
  importRowsLine,
  monthlyCreditsLine,
  orgProfileHelp,
  orgProfileLabel,
  orgProfilePlaceholder,
  overLimitNote,
  quickLooksLine,
} from '../lib/copy';
import { overBy } from '../lib/prose-limit';
import { orgHasProtectionAccounts } from '../lib/vertical';
import { CharCount } from '../components/ui/char-count';
import { ErrorLine } from '../components/ui/error-line';
import { WorkingButton } from '../components/ui/working-button';
import { CreditStandingLine } from '../components/credit-notice';
import { creditStanding } from '../lib/credit-standing';
import { Loading } from '../components/ui/loading';
import { EmptyLine } from '../components/ui/empty-line';
import { TopBar } from '../components/ui/top-bar';
import './settings.css';

/**
 * The org's settings, on one page: how cautious research runs are, and **one
 * paragraph of prose in the org's own voice**. Two controls, and they are the two
 * the office owns.
 *
 * **What is deliberately not here (2026-09-04, Soroush): anything about research
 * depth, model, effort or spend.** Credit is the exception the credits launch spec
 * (2026-09-26) asks for: an account enrolled in live charging sees its balance and
 * this month's quick looks, which are what its own presses spend. The spend limit left this page the day before
 * — the engine checks a cap between rounds and never aborts a leg already
 * running, so a run overshoots by one wave, and the office would reasonably read
 * a ceiling as absolute — and the rest of the bag followed for the general
 * version of the same thought: it prices the pipeline, which is the operator's
 * question. They are admin-plane controls (`PATCH /admin/orgs/:orgId/settings`),
 * and this page shows them in no form at all, read-only included: a number with
 * no way to move it is a question the office cannot answer. **The settings read
 * does not carry them either**: `OrgSettingsView` has no `research` key, so
 * there is nothing on this page's data to render by accident.
 *
 * Which paragraph is the vertical's (verticals note §0.8, amended 2026-09-03 and
 * 2026-09-16): a club writes what it values, which `club_fit` reads; a business
 * org writes what the company is, which `relationship_read` reads; a wealth
 * office writes what it does and who it serves, which `wealth_fit` reads. A
 * vertical is asked only for the paragraph something it derives will open — a
 * club sees no organization profile, because nothing a club run derives would
 * ever open it, and a personal org sees neither field. The business and wealth
 * verticals are asked for the *same core key* under different copy
 * (`orgProfileLabel`, `orgProfileHelp`, `orgProfilePlaceholder`), which is one
 * paragraph read two ways rather than two settings. The `org_profile` setting
 * itself stays core and untouched; what is per-vertical is whether this page
 * asks for it (`orgProfileHasReader`) and in whose words. Sensitivity stays at
 * the top: it is a knob rather than prose, and it was there first.
 *
 * Both are org-level and both are read at run creation, so they say so: a run
 * already underway carries the settings it started with, and nothing here
 * reaches back into it.
 *
 * This is not the per-fact sensitivity toggle (ui-design §9.3), which is out of
 * stage 1 and stays out.
 */

export interface Draft {
  orgProfile: string;
  clubValues: string;
  sensitivity: AdjudicationSensitivity;
}

/**
 * One capped prose field: the textarea, the live count under it, and — only past
 * the cap — the line saying how far.
 *
 * The count has two states and no third: at or under the cap it is a fact in the
 * page's ordinary ink, and past the cap it is red. There is no approaching
 * shade in between — a field with room left is not a problem, and colouring it
 * as one asks the office to answer something that is not wrong yet.
 *
 * There is deliberately **no `maxLength`** on the textarea. It was there before
 * the count was, and it made the over-cap state unreachable: the browser
 * silently truncates a paste at the cap, so a long paragraph pasted in lost its
 * tail with nothing on screen to say so. Refusing the save and saying why is the
 * honest version of the same rule.
 */
function ProseField({
  id,
  label,
  cap,
  value,
  placeholder,
  help,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  cap: number;
  value: string;
  placeholder: string;
  help: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const length = value.trim().length;
  const over = overBy(value, cap);

  /* The count and the over-line are the field's own description, so they are
     named as such rather than left as ink beside it: red text and a Save that
     has gone quiet are the whole signal otherwise, and neither reaches a reader
     who is not looking at the colour. `aria-invalid` is what says the field is
     the reason the button is disabled. Not a live region on purpose — the count
     moves on every keystroke, and a count that announced itself each time would
     be unusable to the reader it was added for. */
  const countId = `${id}-count`;
  const overId = `${id}-over`;

  return (
    <div className="set-row">
      <div className="set-what">
        <h3>
          <label htmlFor={id}>{label}</label>
        </h3>
        <p>{help}</p>
      </div>
      <div className={over > 0 ? 'set-control field over' : 'set-control field'}>
        <textarea
          id={id}
          rows={8}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          aria-invalid={over > 0 || undefined}
          aria-describedby={over > 0 ? `${countId} ${overId}` : countId}
          onChange={(event) => onChange(event.target.value)}
        />
        <CharCount id={countId} length={length} cap={cap} />
        {over > 0 ? (
          <div id={overId} className="field-note over-note">
            {overLimitNote(over)}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const CLUB_VALUES_LABEL = 'Club profile';

const CLUB_VALUES_PLACEHOLDER =
  'e.g. We value long-term members who contribute to club life beyond the course…';

const CLUB_VALUES_HELP = 'Research assesses club fit against this. Applies to new research.';

const SENSITIVITY_HELP = 'How readily research flags findings for review. Applies to new runs.';

/**
 * The form itself, given a draft rather than owning one — which is what makes
 * the over-cap state testable: it is only ever reached by typing, and a saved
 * value can never be over the cap because the server refuses it.
 *
 * `changed` is the caller's answer to "is there anything to save"; whether the
 * draft *may* be saved is this component's, because it is the one that knows
 * which fields are on the page. A business org has no club values and a club org
 * has no organisation profile, so a stale over-long paragraph in either half of
 * a draft cannot lock a Save button for a field that is not on the screen.
 *
 * Nothing else about the save has to change for the absent field. The draft's
 * `orgProfile` starts as the saved value and only an edit moves it, so a page
 * that never renders the control cannot report the field as changed — and the
 * route's PATCH names only what changed, so the key is never sent.
 */
export function SettingsForm({
  vertical,
  draft,
  changed,
  saving,
  saveError,
  failed,
  onEdit,
  onSubmit,
}: {
  vertical: string;
  draft: Draft;
  changed: boolean;
  saving: boolean;
  saveError?: unknown;
  failed?: boolean;
  onEdit: (change: Partial<Draft>) => void;
  onSubmit: () => void;
}) {
  const showsClubValues = vertical === 'club';
  const showsOrgProfile = orgProfileHasReader(vertical);
  const over =
    (showsOrgProfile && overBy(draft.orgProfile, ORG_PROFILE_MAX_LENGTH) > 0) ||
    (showsClubValues && overBy(draft.clubValues, CLUB_VALUES_MAX_LENGTH) > 0);

  return (
    <section className="set-group">
      <h2>Research</h2>
      <div className="set-row">
        <div className="set-what">
          <h3>Sensitivity</h3>
          <p>{SENSITIVITY_HELP}</p>
        </div>
        <div className="set-control">
          <div className="segment">
            {ADJUDICATION_SENSITIVITIES.map((candidate) => (
              <button
                key={candidate}
                type="button"
                className={candidate === draft.sensitivity ? 'seg-option on' : 'seg-option'}
                aria-pressed={candidate === draft.sensitivity}
                disabled={saving}
                onClick={() => onEdit({ sensitivity: candidate })}
              >
                {ADJUDICATION_SENSITIVITY_LABELS[candidate]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {showsOrgProfile ? (
        <ProseField
          id="st-org-profile"
          label={orgProfileLabel(vertical)}
          cap={ORG_PROFILE_MAX_LENGTH}
          value={draft.orgProfile}
          placeholder={orgProfilePlaceholder(vertical)}
          help={orgProfileHelp(vertical)}
          disabled={saving}
          onChange={(orgProfile) => onEdit({ orgProfile })}
        />
      ) : null}

      {showsClubValues ? (
        <ProseField
          id="st-club-values"
          label={CLUB_VALUES_LABEL}
          cap={CLUB_VALUES_MAX_LENGTH}
          value={draft.clubValues}
          placeholder={CLUB_VALUES_PLACEHOLDER}
          help={CLUB_VALUES_HELP}
          disabled={saving}
          onChange={(clubValues) => onEdit({ clubValues })}
        />
      ) : null}

      <div className="set-save">
        <WorkingButton
          label="Save"
          workingLabel="Saving…"
          working={saving}
          disabled={!changed || over}
          onClick={onSubmit}
        />
        {!changed && !saving ? <span className="set-save-note">No changes</span> : null}
        {failed ? <ErrorLine error={saveError} /> : null}
      </div>
    </section>
  );
}

/**
 * The form's view of the saved settings. An unset sensitivity shows as the
 * server's own floor, read from the same constant the resolver uses, because
 * that is what a run would actually use — the null is honest on the wire and
 * pointless on screen.
 */
const draftOf = (settings: OrgSettingsView): Draft => ({
  /* Required on the view and nullable: `null` is "nobody has written one", which
     is an empty textarea. A vertical-owned key can also be *absent*, which is
     why the club's own field reads through the same `??` — the two states look
     identical in a form and mean different things on the wire. */
  orgProfile: settings.org_profile ?? '',
  clubValues: settings.club_values ?? '',
  sensitivity: settings.adjudication_sensitivity_default ?? DEFAULT_ADJUDICATION_SENSITIVITY,
});

export function Settings() {
  const org = useOrg();
  const settings = useOrgSettings();
  const save = useUpdateOrgSettings();

  // Null until the office touches something: the saved settings are the form
  // until then, so a save leaves the page showing what the server just answered.
  // Which is also why every control disables while the save is in flight — a
  // keystroke landing mid-write would be dropped by that same reset.
  const [draft, setDraft] = useState<Draft | null>(null);

  if (settings.isPending) {
    return (
      <div className="column">
        <EmptyLine>
          <Loading />
        </EmptyLine>
      </div>
    );
  }

  if (settings.isError) {
    return (
      <div className="column settings">
        <div className="head">
          <div className="titles">
            <div className="title">Settings</div>
          </div>
        </div>
        <p className="lede">{loadErrorLine(settings.error)}</p>
      </div>
    );
  }

  const saved = draftOf(settings.data);
  const current = draft ?? saved;
  const profileChanged = current.orgProfile.trim() !== saved.orgProfile;
  const valuesChanged = current.clubValues.trim() !== saved.clubValues;
  const sensitivityChanged = current.sensitivity !== saved.sensitivity;
  const changed = profileChanged || valuesChanged || sensitivityChanged;

  const edit = (change: Partial<Draft>) => setDraft({ ...current, ...change });

  const submit = () => {
    if (!changed || save.isPending) return;
    const body: OrgSettingsUpdate = {};
    // Only what moved is sent: the settings bag holds keys this page has never
    // heard of, and a PATCH that named everything would be a form that owned them.
    /* An emptied textarea is sent as the empty string, not as `null`: the
       profile has one spelling for "unset" (blank after trimming), and the
       server deletes the key rather than storing it. The club's values keep
       their `null` because they accept both. */
    if (profileChanged) body.org_profile = current.orgProfile.trim();
    if (valuesChanged) {
      const trimmed = current.clubValues.trim();
      body.club_values = trimmed.length === 0 ? null : trimmed;
    }
    if (sensitivityChanged) body.adjudication_sensitivity_default = current.sensitivity;

    save.mutate(body, {
      onSuccess: () => {
        setDraft(null);
      },
    });
  };

  return (
    <div className="column settings">
      <TopBar parent={{ to: '/', label: 'Home' }} here="Settings" />

      <div className="head">
        <div className="titles">
          <div className="title">Settings</div>
        </div>
      </div>

      {/* **The standard form, in every vertical** (security fold, 2026-09-22).
          A protection firm has the same org settings every other office has;
          what it also has is engagements, and this is the way to them from the
          page a reader looking for "where do I change things" opens first. */}
      <SettingsForm
        vertical={org.vertical}
        draft={current}
        changed={changed}
        saving={save.isPending}
        saveError={save.error}
        failed={save.isError}
        onEdit={edit}
        onSubmit={submit}
      />

      <ConnectorSection />

      <CreditsSection />

      {orgHasProtectionAccounts(org) ? (
        <section className="set-group">
          <h2>Protection accounts</h2>
          <div className="set-row">
            <div className="set-what">
              <p>Group people and organizations and manage their shared context.</p>
            </div>
            <div className="set-control">
              <Link className="btn act" to="/accounts">
                Manage protection accounts
              </Link>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}

/**
 * **The Claude connector's three strings**, so a member can connect without
 * asking an operator. Renders nothing while it loads, if the read fails, or when
 * this deployment has no connector URL mounted.
 */
export function ConnectorSection() {
  const connector = useConnector();
  return connector.data ? <ConnectorPanel connector={connector.data} /> : null;
}

export function ConnectorPanel({ connector }: { connector: ConnectorView }) {
  if (connector.url === null) return null;
  const lines: [string, string | null][] = [
    ['Connector URL', connector.url],
    ['Client ID', connector.client_id],
    ['Client secret', connector.client_secret],
  ];
  return (
    <section className="set-group">
      <h2>Claude connector</h2>
      <div className="set-row">
        <div className="set-what">
          <p>
            In Claude, open Settings › Connectors › Add custom connector. Paste the URL, and the ID
            and secret under Advanced settings. Connect and sign in. On Team and Enterprise plans,
            an Owner adds it once for everyone.
          </p>
        </div>
        <dl className="set-control connector-strings">
          {lines.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>
                {value === null ? (
                  <span className="quiet">Not configured</span>
                ) : (
                  <code>{value}</code>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/**
 * **Credit, for an account enrolled in live charging** (credits launch spec, step
 * 1): the balance, this month's quick looks and the recent charges. Renders nothing
 * for everybody else, and nothing while it loads or if the read fails: the page's
 * own settings do not wait on it.
 */
export function CreditsSection() {
  const credits = useCredits();
  return credits.data ? <CreditsPanel summary={credits.data} /> : null;
}

/** The reader's own calendar day for a timestamp, in the page's date style. */
const localDay = (at: string): string =>
  new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export function CreditsPanel({ summary: data }: { summary: CreditSummary }) {
  if (!data.charging_enabled) return null;
  const perCredit = data.units_per_credit;
  return (
    <section className="set-group credits">
      <h2>Credits</h2>
      <p className="credit-balance">
        {data.plan.label} plan · {creditAmount(data.balance.available, perCredit)} available
        {data.balance.held > 0
          ? ` · ${creditAmount(data.balance.held, perCredit)} held for work in progress`
          : ''}
      </p>
      <CreditStandingLine standing={creditStanding(data)} />
      <ul className="credit-lines">
        {data.monthly_credits ? (
          <li>{monthlyCreditsLine(data.monthly_credits, perCredit)}</li>
        ) : null}
        <li>{quickLooksLine(data.quick_looks.used, data.quick_looks.included)}</li>
        <li>{importRowsLine(data.import_rows.used, data.import_rows.included)}</li>
      </ul>
      {data.recent.length > 0 ? (
        <>
          <h3 className="credit-recent-head">Recent work</h3>
          <table className="table record-table credit-recent">
            <thead>
              <tr>
                <th scope="col">Record</th>
                <th scope="col">Work</th>
                <th scope="col">Date</th>
                <th scope="col">Charge</th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((charge) => {
                const [amount, standing] = splitStanding(chargeStanding(charge, perCredit));
                return (
                  <tr key={charge.id}>
                    <td className={charge.subject_name ? 'record' : 'record removed'}>
                      {charge.subject_name ?? 'Removed record'}
                    </td>
                    <td>{CREDIT_OPERATION_LABELS[charge.operation]}</td>
                    <td className="date">{localDay(charge.created_at)}</td>
                    <td className="charge">
                      {amount}
                      {standing ? <span className="standing"> · {standing}</span> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      ) : null}
    </section>
  );
}

/** "20 credits · Charged" as the amount and, quieter beside it, what became of it. */
function splitStanding(line: string): [string, string | null] {
  const at = line.indexOf(' · ');
  return at === -1 ? [line, null] : [line.slice(0, at), line.slice(at + 3)];
}
